import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useMessageGroupAdminMutations } from "../hooks/useMessageGroupAdminMutations";
import { MessageGroupSummary } from "../messageGroups.types";
import { MemberPicker, PickableMember, buildContactMember, memberKeyOf, toE164 } from "./MemberPicker";

interface AddMembersModalProps {
  open: boolean;
  group?: MessageGroupSummary | null;
  companyId?: number | string | null;
  isSaving?: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onConfirm: (members: PickableMember[]) => Promise<void> | void;
}

export const AddMembersModal = ({ open, group, companyId, isSaving, errorMessage, onClose, onConfirm }: AddMembersModalProps) => {
  const [members, setMembers] = useState<PickableMember[]>([]);
  const [manualNumber, setManualNumber] = useState("");
  const [manualError, setManualError] = useState<string | null>(null);

  const isSmsGroup = group?.channel === "sms";
  const { validateNumbers } = useMessageGroupAdminMutations(undefined, companyId ?? group?.companyId);
  const excludeKeys = (group?.members ?? []).map(memberKeyOf);

  const handleClose = () => {
    setMembers([]);
    setManualNumber("");
    setManualError(null);
    onClose();
  };

  const handleAddManualNumber = useCallback(async () => {
    const phoneNumber = toE164(manualNumber);

    if (!phoneNumber) {
      setManualError("Not a valid US or Canada number: 10 digits and the area code cannot start with 0 or 1");
      return;
    }

    const alreadyIn =
      members.some((member) => member.uid === phoneNumber) ||
      excludeKeys.includes(`contact:${phoneNumber}`) ||
      excludeKeys.includes(`companyNumber:${phoneNumber}`);

    if (alreadyIn) {
      setManualError("That number is already in the group");
      return;
    }

    try {
      const [validation] = await validateNumbers.mutateAsync([phoneNumber]);

      if (validation && !validation.canJoinGroup) {
        setManualError(validation.reason ?? "This number cannot join a group text");
        return;
      }

      setManualError(null);
      setManualNumber("");
      setMembers((current) => [
        ...current,
        buildContactMember(phoneNumber, validation?.contactName ?? null, Boolean(validation?.isFlownectNumber)),
      ]);
    } catch {
      setManualError("Could not verify this number, try again");
    }
  }, [manualNumber, members, excludeKeys, validateNumbers]);

  const handleConfirm = async () => {
    await onConfirm(members);
    setMembers([]);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth sx={{ zIndex: 2147483000 }}>
      <DialogTitle sx={{ pb: 1, fontSize: 17, fontWeight: 700 }}>
        {group?.channel === "sms" ? "Add numbers" : "Add members"}
      </DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        {isSmsGroup && (
          <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
            <TextField
              value={manualNumber}
              onChange={(event) => setManualNumber(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddManualNumber();
                }
              }}
              placeholder="(305) 555-1234"
              label="Add a number"
              size="small"
              fullWidth
              error={Boolean(manualError)}
              helperText={manualError}
            />
            <Button
              onClick={handleAddManualNumber}
              size="small"
              variant="outlined"
              disabled={validateNumbers.isPending}
              sx={{ alignSelf: "flex-start", height: 40, minWidth: 72 }}
            >
              {validateNumbers.isPending ? <CircularProgress size={16} /> : "Add"}
            </Button>
          </Box>
        )}

        <MemberPicker
          selected={members}
          onChange={setMembers}
          mode={group?.channel === "sms" ? "sms" : "internal"}
          companyId={companyId ?? group?.companyId}
          excludeKeys={excludeKeys}
          emptyLabel={group?.channel === "sms" ? "No SMS contacts left to add" : "Everyone is already in this group"}
        />
        {errorMessage && <Typography sx={{ mt: 1, fontSize: 12, color: "error.main" }}>{errorMessage}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} size="small" color="inherit">
          Cancel
        </Button>
        <Button onClick={handleConfirm} size="small" variant="contained" disabled={members.length === 0 || isSaving}>
          {isSaving ? "Adding..." : `Add ${members.length || ""}`.trim()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
